import { DollarSign, Mic, Search, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { TSearchDealItem, TSearchLeadItem, TSearchRecordingItem } from "@/services/search.service";
import { avatarColor } from "@/utils/call-center.utils";
import { fCurrency } from "@/utils/formatNumber";
import { getInitials } from "@/utils/strings";
import { KindBadge } from "./result-row";
import { SearchPage } from "./types";

/** Inner content (icon, title, subtitle, kind badge) of each result type. */

const formatDuration = (seconds?: number) => {
  if (seconds == null || isNaN(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const StatusChip = ({ status, upper }: { status?: { label: string; color: string } | null; upper?: boolean }) =>
  status ? (
    <span
      className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold", upper && "uppercase tracking-wider")}
      style={{ color: status.color, background: `${status.color}1A` }}
    >
      {status.label}
    </span>
  ) : null;

/** "a · b · c" with the first part optionally highlighted. */
const Parts = ({ parts, highlightFirst }: { parts: string[]; highlightFirst?: boolean }) => (
  <>
    {parts.map((part, i) => (
      <span key={i}>
        {i > 0 && <span className="text-slate-700"> · </span>}
        {i === 0 && highlightFirst ? <span className="tabular-nums text-slate-300">{part}</span> : part}
      </span>
    ))}
  </>
);

const Body = ({ title, chip, sub }: { title: string; chip?: React.ReactNode; sub: React.ReactNode }) => (
  <div className="min-w-0 flex-1">
    <div className="flex items-center gap-2">
      <span className="truncate text-[12.5px] font-semibold text-white">{title}</span>
      {chip}
    </div>
    <div className="truncate text-[10.5px] text-slate-500">{sub}</div>
  </div>
);

export const PageContent = ({ page }: { page: SearchPage }) => {
  const Icon = page.icon ?? Search;
  return (
    <>
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.04] text-slate-300">
        <Icon className="h-4 w-4" />
      </div>
      <Body title={page.title} sub={page.path} />
      <KindBadge label="Page" />
    </>
  );
};

export const LeadContent = ({ lead }: { lead: TSearchLeadItem }) => (
  <>
    <div
      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[10px] font-semibold text-white ring-1 ring-white/10"
      style={{ background: avatarColor(lead.title || "?") }}
    >
      {getInitials(lead.title || "?")}
    </div>
    <Body title={lead.title} chip={<StatusChip status={lead.status} />} sub={lead.subtitle || lead.meta?.email} />
    <KindBadge label="Lead" />
  </>
);

export const RecordingContent = ({ recording }: { recording: TSearchRecordingItem }) => {
  const isVideo = recording.meta?.channel?.toLowerCase() === "video";
  const parts = [formatDuration(recording.meta?.durationSeconds), recording.meta?.agentName, recording.meta?.leadName].filter(
    Boolean
  ) as string[];
  return (
    <>
      <div
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1",
          isVideo ? "bg-accent-purple/10 text-accent-purple ring-accent-purple/20" : "bg-accent-green/10 text-accent-green ring-accent-green/20"
        )}
      >
        {isVideo ? <Video className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </div>
      <Body
        title={recording.title}
        chip={<StatusChip status={recording.status} upper />}
        sub={parts.length > 0 ? <Parts parts={parts} /> : recording.subtitle}
      />
      <KindBadge label="Recording" />
    </>
  );
};

export const DealContent = ({ deal }: { deal: TSearchDealItem }) => {
  const meta = deal.meta;
  const amount = meta?.amount != null ? `${fCurrency(meta.amount)} ${meta.currency ?? ""}`.trim() : null;
  const parts = [amount, meta?.pipelineName, meta?.ownerName].filter(Boolean) as string[];
  return (
    <>
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent-green/10 text-accent-green ring-1 ring-accent-green/20">
        <DollarSign className="h-4 w-4" />
      </div>
      <Body
        title={deal.title}
        chip={<StatusChip status={deal.status} upper />}
        sub={parts.length > 0 ? <Parts parts={parts} highlightFirst={amount != null} /> : deal.subtitle}
      />
      <KindBadge label="Deal" />
    </>
  );
};
